import { useState, useEffect } from "react";
import {
  Outlet,
  useNavigate,
  useLocation,
  NavLink,
  useLoaderData,
} from "react-router-dom";
import {
  DashboardOutlined,
  TeamOutlined,
  UserOutlined,
  MenuOutlined,
  PartitionOutlined,
  DownloadOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import {
  Layout,
  Menu,
  Avatar,
  Typography,
  Modal,
  Space,
  Button,
  Grid,
} from "antd";
import logo from "./assets/new_vtkd_logo.png";
import { getCookie, logoutUser } from "./apiBridge";
import { UserInfo } from "./interface";
import { OrganizationProvider } from "./landing/organization/OrganizationContext";

const { Header, Content, Sider } = Layout;
const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

const LoginChecker = () => {
  const [logoutDisplay, setLogoutDisplay] = useState('none');
  const navigate = useNavigate();

  const back = async () => {
    setLogoutDisplay("none");
    navigate("/login");
  };

  useEffect(() => {
    const checkLogin = () => {
      if (getCookie("token") === "") {
        setLogoutDisplay("flex");
        return false;
      }
    }
      
    checkLogin();
    
    const timer = setInterval(checkLogin, 1000 * 5);
    
    return () => {
      clearInterval(timer);
    };
  }, []);
  


  return (
      <>
        <div style={{
          display: logoutDisplay,
          position: "fixed",
          top: 0,
          padding: "12px 24px",
          width: "100%",
          background: "lightyellow",
          color: "rgba(0, 0, 0, 0.88)",
          borderBottom: "1px solid #f0c36d",
          alignItems: "center",
          justifyContent: "space-between",
          zIndex: 1000
        }}>
           <span>
             <strong>Your session has expired.</strong>
             {" "}Please log in again.
           </span>
           <Button type="primary" size="small" onClick={back}>Back to login</Button>
        </div>
      </>
  )
}

const RootLayout = () => {
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const data = useLoaderData() as UserInfo;
  const { role, alias } = data;
  //console.log("Root layout");

  const [logoutConfirmModal, setLogoutConfirmModal] = useState(false);
  const [collapsedSider, setCollapsedSider] = useState(!screens.md);
  const [logoutHover, setLogoutHover] = useState(false);

  const logoutIcon = (
    <img
      src="/logout.png"
      alt="logout"
      style={{
        width: 24,
        height: 24,
        filter: logoutHover ? 'none' : 'grayscale(90%)',
        transition: 'filter 0.2s',
        verticalAlign: 'middle',
      }}
      onMouseEnter={() => setLogoutHover(true)}
      onMouseLeave={() => setLogoutHover(false)}
    />
  );

  const doLogoutUser = async () => {
    let res = await logoutUser()
    if (res) {
      navigate("/login");
    }
  };

  const getSelectedNavKey = (pathname: string): string => {
    if (pathname.startsWith('/activity')) {
      return 'tournament';
    }
    return pathname.split("/")[1];
  };

  const navList: MenuProps["items"] = [
    {
      key: "dashboard",
      icon: <DashboardOutlined />,
      label: <NavLink to="/">Dashboard</NavLink>,
    },

    {
      key: "users",
      icon: <TeamOutlined />,
      label: <NavLink to="/users">Member Management</NavLink>,
      style: { display: role === "Owner" || role === "GameMaster" ? "block" : "none" },
    },
    {
      key: "subscription",
      icon: <CreditCardOutlined />,
      label: <NavLink to="/subscription">Subscription</NavLink>,
      style: { display: role === "Owner" ? "block" : "none" },
    },

    {
      key: "tournament",
      icon: <PartitionOutlined />,
      label: <NavLink to="/activity">Tournament</NavLink>,
    },
    {
      key: "settings",
      icon: <UserOutlined />,
      label: <NavLink to="/settings">My Profile</NavLink>,
    },

    {
      key: "game",
      icon: <DownloadOutlined />,
      label: <NavLink to="/game">Game Downloads</NavLink>,
    },
  ];

  return (
    <Layout>
      <Modal
        footer={null}
        maskClosable={false}
        closeIcon={false}
        centered
        open={logoutConfirmModal}
        width={380}
      >
        <div style={{ marginBottom: 20 }}>
          <Title level={4}>Log Out</Title>
          <Text type="secondary">Are you sure you want to log out?</Text>
        </div>
        <div style={{ textAlign: "right" }}>
          <Space>
            <Button onClick={() => setLogoutConfirmModal(false)}>Cancel</Button>

            <Button type="primary" onClick={doLogoutUser}>
              Log Out
            </Button>
          </Space>
        </div>
      </Modal>
      <LoginChecker />

      <Header
        style={{
          display: "flex",
          alignItems: "center",
          background: "white",
          borderBottom: "1px solid #d3d3d3",
          zIndex: 200,
          padding: screens.md ? undefined : "0 20px",
        }}
      >
        <img src={logo} alt="vtkd_logo" style={{ height: 32 }} />
        <div style={{ width: "100%" }}>
          {screens.md ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Avatar
                icon={<UserOutlined />}
                style={{ marginRight: 10, backgroundColor: "#40a9ff" }}
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Text strong>{alias}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {role === "GameMaster"
                    ? "Game Master"
                    : role === "User"
                    ? "Player"
                    : role === "Owner"
                    ? "System Operator"
                    : role}
                </Text>
              </div>
              {screens.md && (
                <Button 
                  type="text" 
                  icon={logoutIcon}
                  onClick={() => setLogoutConfirmModal(true)}
                  style={{ marginLeft: 10 }}
                  onMouseEnter={() => setLogoutHover(true)}
                  onMouseLeave={() => setLogoutHover(false)}
                />
              )}
            </div>
          ) : (
            <MenuOutlined
              style={{ cursor: "pointer", float: "right" }}
              onClick={() => setCollapsedSider(!collapsedSider)}
            />
          )}
        </div>
      </Header>

      <Layout>
        <Sider
          width={240}
          breakpoint="md"
          collapsedWidth={0}
          collapsed={screens.md ? false : collapsedSider}
          trigger={null}
          style={{
            boxShadow: `0 0 8px 0 ${screens.md ? "#cbcbcb" : "#3b3b3b"}`,
            height: "calc(100vh - 64px)",
            backgroundColor: "#ffffff",
            position: "absolute",
            zIndex: 2,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "100%",
            }}
          >
            <Menu
              mode="inline"
              selectedKeys={[getSelectedNavKey(location.pathname)]}
              style={{ borderRight: 0 }}
              items={navList}
              onClick={() => setCollapsedSider(true)}
            />
            {!screens.md && (
              <Menu
                mode="inline"
                selectedKeys={[getSelectedNavKey(location.pathname)]}
                style={{ borderRight: 0, borderTop: "1px solid #f5f5f5" }}
                items={[
                  { key: "logout", icon: logoutIcon, label: "Logout" },
                ]}
                onClick={() => {
                  setCollapsedSider(true);
                  setLogoutConfirmModal(true);
                }}
              />
            )}
          </div>
        </Sider>
        <Layout
          style={{
            transition: "all 0.2s,background 0s",
            marginLeft: screens.md ? 240 : 0,
          }}
        >
          {!screens.md && !collapsedSider && (
            <div
              className="mask"
              style={{
                height: "calc(100vh - 64px)",
                position: "absolute",
                width: "100%",
                backgroundColor: "#00000073",
                zIndex: 1,
              }}
              onClick={() => setCollapsedSider(true)}
            />
          )}
          <Content>
            <div
              style={{
                padding: 20,
                height: "calc(100vh - 64px)",
                overflowY: "auto",
              }}
            >
              <Outlet context={data} />
            </div>
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

const RootLayoutWithProvider = () => {
  return (
    <OrganizationProvider>
      <RootLayout />
    </OrganizationProvider>
  );
};

export default RootLayoutWithProvider;
